import {
  Controller,
  Control,
  UseFormGetValues,
  FieldErrors,
} from 'react-hook-form';
import {
  Concentration,
  ConcentrationFormData,
} from '../../../../interfaces/concentration';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DecimalInput from '../../../../components/UI/DecimalInput/DecimalInput';
import ReactTooltip from 'react-tooltip';
import {
  calculateDoseRateHardLimits,
  wrongLimits,
} from '../../../../helpers/concentrations';
import {
  calculateVariablePrecision,
  validatePrecision,
} from '../../../../helpers/drugs';

/**
 * @description Props for the DoseRateFields component
 * @typedef {Object} DoseRateFieldsProps
 */
type DoseRateFieldsProps = {
  /** The concentration record being edited */
  record: Concentration;
  /** React Hook Form control object */
  control: Control<ConcentrationFormData, any>;
  /** Function to get current form values */
  getValues: UseFormGetValues<ConcentrationFormData>;
  /** Form validation errors */
  errors: FieldErrors<ConcentrationFormData>;
  /** Selected dose mode unit */
  watchDoseModeUnit:
    | {
        label: string;
        value: string;
      }
    | null
    | undefined;
  /** Selected dose mode time */
  watchDoseModeTime:
    | {
        label: string;
        value: string;
      }
    | null
    | undefined;
  /** Whether the dose is weight-based */
  watchWeightBased: boolean;
  /** Concentration lower hard limit */
  watchConcentrationLHL: string | number | null | undefined;
  /** Concentration upper hard limit */
  watchConcentrationUHL: string | number | null | undefined;
  /** Dose rate lower hard limit */
  watchDoseRateLHL: string | number | null | undefined;
  /** Dose rate lower soft limit */
  watchDoseRateLSL: string | number | null | undefined;
  /** Dose rate upper soft limit */
  watchDoseRateUSL: string | number | null | undefined;
  /** Dose rate upper hard limit */
  watchDoseRateUHL: string | number | null | undefined;
  /** Whether the user has permission to manage this concentration */
  canManage: boolean;
};

/**
 * @description Component for rendering dose rate limit fields with validation.
 * Calculates precision for each field and validates limits are in correct order.
 *
 * @SADtrace SAD-003-4.1, SAD-003-4.2, SAD-003-4.3
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {DoseRateFieldsProps} props - Component props
 *
 * @returns {JSX.Element} Rendered component
 */
const DoseRateFields: FC<DoseRateFieldsProps> = ({
  record,
  control,
  getValues,
  errors,
  watchDoseModeUnit,
  watchDoseModeTime,
  watchWeightBased,
  watchConcentrationLHL,
  watchConcentrationUHL,
  watchDoseRateLHL,
  watchDoseRateLSL,
  watchDoseRateUSL,
  watchDoseRateUHL,
  canManage,
}) => {
  const { t } = useTranslation();

  const precisionDoseRateLHL = useMemo<any>(
    () => calculateVariablePrecision(watchDoseRateLHL),
    [watchDoseRateLHL],
  );
  const precisionDoseRateLSL = useMemo<any>(
    () => calculateVariablePrecision(watchDoseRateLSL),
    [watchDoseRateLSL],
  );
  const precisionDoseRateUSL = useMemo<any>(
    () => calculateVariablePrecision(watchDoseRateUSL),
    [watchDoseRateUSL],
  );
  const precisionDoseRateUHL = useMemo<any>(
    () => calculateVariablePrecision(watchDoseRateUHL),
    [watchDoseRateUHL],
  );

  const doseRateLimits: { min: number; max: number } | null | undefined =
    calculateDoseRateHardLimits(
      record,
      watchWeightBased,
      record.is_complete ? record.dose_mode_unit : watchDoseModeUnit?.value,
      record.is_complete ? record.dose_mode_time : watchDoseModeTime?.value,
      watchConcentrationLHL,
      watchConcentrationUHL,
    );

  return (
    <div className="position-relative row">
      {!canManage && (
        <div className="device-concentration--disabled-item"></div>
      )}
      <div>
        <div
          className={`row ${
            !wrongLimits(doseRateLimits) ? 'pb-3' : ''
          } pt-1 flex-nowrap`}
        >
          <div className="device-concentration-form__label">
            {t('device.dose_rate')}
          </div>

          {/* Lower hard limit */}
          <div className="device-concentration__box-input">
            <Controller
              control={control}
              name="dose_rate_lower_hard_limit"
              // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
              rules={{
                required: true,
                min: doseRateLimits?.min,
                max: doseRateLimits?.max,
                validate: {
                  increasingOrder: (value) => {
                    if (!value || isNaN(Number(value))) return true;

                    const currentValue = Number(value);
                    let isValid = true;

                    const [LSL, USL, UHL] = getValues([
                      'dose_rate_lower_soft_limit',
                      'dose_rate_upper_soft_limit',
                      'dose_rate_upper_hard_limit',
                    ]);

                    isValid =
                      isValid && (LSL ? Number(LSL) > currentValue : true);
                    isValid =
                      isValid && (USL ? Number(USL) > currentValue : true);
                    isValid =
                      isValid && (UHL ? Number(UHL) > currentValue : true);

                    return isValid;
                  },
                  precision: (value) =>
                    validatePrecision(value, precisionDoseRateLHL),
                },
              }}
              // v8 ignore stop -- WAIVER(THIRDPARTY)
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <DecimalInput
                  value={String(value)}
                  onChange={onChange}
                  onBlur={onBlur}
                  precision={precisionDoseRateLHL}
                  rounding={'toAllowedNumberOfDecimals'}
                  className={
                    'device-concentration__input--hard-limit ' +
                    (errors?.dose_rate_lower_hard_limit ? 'has-error' : '')
                  }
                  ref={ref}
                  data-tip
                  data-for="dose_rate_lower_hard_limit"
                  data-testid="dose_rate_lower_hard_limit"
                />
              )}
            />

            {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact; per-error-type message branches are display-only and depend on validation states not all reachable in an isolated unit render; error display is asserted by ConcentrationForm form-level tests */}
            {errors?.dose_rate_lower_hard_limit && (
              <ReactTooltip
                id="dose_rate_lower_hard_limit"
                effect="solid"
                place="bottom"
                delayHide={300}
                type="error"
              >
                {errors?.dose_rate_lower_hard_limit?.type === 'required' &&
                  t('errors.field__required')}
                {errors?.dose_rate_lower_hard_limit?.type === 'min' &&
                  t('device.errors.not_lower_than', {
                    value: doseRateLimits?.min,
                  })}
                {errors?.dose_rate_lower_hard_limit?.type === 'max' &&
                  t('device.errors.not_greater_than', {
                    value: doseRateLimits?.max,
                  })}
                {errors?.dose_rate_lower_hard_limit?.type ===
                  'increasingOrder' && t('device.errors.order_LHL_LSL_USL_UHL')}
                {errors?.dose_rate_lower_hard_limit?.type === 'precision' &&
                  t('device.errors.precision', {
                    value: precisionDoseRateLHL,
                  })}
              </ReactTooltip>
            )}
            {/* v8 ignore stop -- WAIVER(MICRO) */}
          </div>

          {/* Lower soft limit */}
          <div className="device-concentration__box-input">
            <Controller
              control={control}
              name="dose_rate_lower_soft_limit"
              // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
              rules={{
                min: doseRateLimits?.min,
                max: doseRateLimits?.max,
                validate: {
                  increasingOrder: (value) => {
                    if (!value || isNaN(Number(value))) return true;

                    const currentValue = Number(value);
                    let isValid = true;

                    const [LHL, USL, UHL] = getValues([
                      'dose_rate_lower_hard_limit',
                      'dose_rate_upper_soft_limit',
                      'dose_rate_upper_hard_limit',
                    ]);

                    isValid =
                      isValid && (LHL ? Number(LHL) < currentValue : true);
                    isValid =
                      isValid && (USL ? Number(USL) > currentValue : true);
                    isValid =
                      isValid && (UHL ? Number(UHL) > currentValue : true);

                    return isValid;
                  },
                  precision: (value) =>
                    validatePrecision(value, precisionDoseRateLSL),
                },
              }}
              // v8 ignore stop -- WAIVER(THIRDPARTY)
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <DecimalInput
                  value={String(value)}
                  onChange={onChange}
                  onBlur={onBlur}
                  precision={precisionDoseRateLSL}
                  rounding={'toAllowedNumberOfDecimals'}
                  className={
                    'device-concentration__input--soft-limit ' +
                    (errors?.dose_rate_lower_soft_limit ? 'has-error' : '')
                  }
                  ref={ref}
                  data-tip
                  data-for="dose_rate_lower_soft_limit"
                  data-testid="dose_rate_lower_soft_limit"
                  placeholder={''}
                />
              )}
            />

            {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact; per-error-type message branches are display-only and depend on validation states not all reachable in an isolated unit render; error display is asserted by ConcentrationForm form-level tests */}
            {errors?.dose_rate_lower_soft_limit && (
              <ReactTooltip
                id="dose_rate_lower_soft_limit"
                effect="solid"
                place="bottom"
                delayHide={300}
                type="error"
              >
                {errors?.dose_rate_lower_soft_limit?.type === 'min' &&
                  t('device.errors.not_lower_than', {
                    value: doseRateLimits?.min,
                  })}
                {errors?.dose_rate_lower_soft_limit?.type === 'max' &&
                  t('device.errors.not_greater_than', {
                    value: doseRateLimits?.max,
                  })}
                {errors?.dose_rate_lower_soft_limit?.type ===
                  'increasingOrder' && t('device.errors.order_LHL_LSL_USL_UHL')}
                {errors?.dose_rate_lower_soft_limit?.type === 'precision' &&
                  t('device.errors.precision', {
                    value: precisionDoseRateLSL,
                  })}
              </ReactTooltip>
            )}
            {/* v8 ignore stop -- WAIVER(MICRO) */}
          </div>

          {/* Upper soft limit */}
          <div className="device-concentration__box-input">
            <Controller
              control={control}
              name="dose_rate_upper_soft_limit"
              // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
              rules={{
                min: doseRateLimits?.min,
                max: doseRateLimits?.max,
                validate: {
                  increasingOrder: (value) => {
                    if (!value || isNaN(Number(value))) return true;

                    const currentValue = Number(value);
                    let isValid = true;

                    const [LHL, LSL, UHL] = getValues([
                      'dose_rate_lower_hard_limit',
                      'dose_rate_lower_soft_limit',
                      'dose_rate_upper_hard_limit',
                    ]);

                    isValid =
                      isValid && (LHL ? Number(LHL) < currentValue : true);
                    isValid =
                      isValid && (LSL ? Number(LSL) < currentValue : true);
                    isValid =
                      isValid && (UHL ? Number(UHL) > currentValue : true);

                    return isValid;
                  },
                  precision: (value) =>
                    validatePrecision(value, precisionDoseRateUSL),
                },
              }}
              // v8 ignore stop -- WAIVER(THIRDPARTY)
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <DecimalInput
                  value={String(value)}
                  onChange={onChange}
                  onBlur={onBlur}
                  precision={precisionDoseRateUSL}
                  rounding={'toAllowedNumberOfDecimals'}
                  className={
                    'device-concentration__input--soft-limit ' +
                    (errors?.dose_rate_upper_soft_limit ? 'has-error' : '')
                  }
                  ref={ref}
                  data-tip
                  data-for="dose_rate_upper_soft_limit"
                  data-testid="dose_rate_upper_soft_limit"
                  placeholder={''}
                />
              )}
            />

            {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact; per-error-type message branches are display-only and depend on validation states not all reachable in an isolated unit render; error display is asserted by ConcentrationForm form-level tests */}
            {errors?.dose_rate_upper_soft_limit && (
              <ReactTooltip
                id="dose_rate_upper_soft_limit"
                effect="solid"
                place="bottom"
                delayHide={300}
                type="error"
              >
                {errors?.dose_rate_upper_soft_limit?.type === 'min' &&
                  t('device.errors.not_lower_than', {
                    value: doseRateLimits?.min,
                  })}
                {errors?.dose_rate_upper_soft_limit?.type === 'max' &&
                  t('device.errors.not_greater_than', {
                    value: doseRateLimits?.max,
                  })}
                {errors?.dose_rate_upper_soft_limit?.type ===
                  'increasingOrder' && t('device.errors.order_LHL_LSL_USL_UHL')}
                {errors?.dose_rate_upper_soft_limit?.type === 'precision' &&
                  t('device.errors.precision', {
                    value: precisionDoseRateUSL,
                  })}
              </ReactTooltip>
            )}
            {/* v8 ignore stop -- WAIVER(MICRO) */}
          </div>

          {/* Upper hard limit */}
          <div className="device-concentration__box-input">
            <Controller
              control={control}
              name="dose_rate_upper_hard_limit"
              // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
              rules={{
                required: true,
                min: doseRateLimits?.min,
                max: doseRateLimits?.max,
                validate: {
                  increasingOrder: (value) => {
                    if (!value || isNaN(Number(value))) return true;

                    const currentValue = Number(value);
                    let isValid = true;

                    const [LHL, LSL, USL] = getValues([
                      'dose_rate_lower_hard_limit',
                      'dose_rate_lower_soft_limit',
                      'dose_rate_upper_soft_limit',
                    ]);

                    isValid =
                      isValid && (LHL ? Number(LHL) < currentValue : true);
                    isValid =
                      isValid && (LSL ? Number(LSL) < currentValue : true);
                    isValid =
                      isValid && (USL ? Number(USL) < currentValue : true);

                    return isValid;
                  },
                  precision: (value) =>
                    validatePrecision(value, precisionDoseRateUHL),
                },
              }}
              // v8 ignore stop -- WAIVER(THIRDPARTY)
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <DecimalInput
                  value={String(value)}
                  onChange={onChange}
                  onBlur={onBlur}
                  precision={precisionDoseRateUHL}
                  rounding={'toAllowedNumberOfDecimals'}
                  className={
                    'device-concentration__input--hard-limit ' +
                    (errors?.dose_rate_upper_hard_limit ? 'has-error' : '')
                  }
                  ref={ref}
                  data-tip
                  data-for="dose_rate_upper_hard_limit"
                  data-testid="dose_rate_upper_hard_limit"
                />
              )}
            />

            {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact; per-error-type message branches are display-only and depend on validation states not all reachable in an isolated unit render; error display is asserted by ConcentrationForm form-level tests */}
            {errors?.dose_rate_upper_hard_limit && (
              <ReactTooltip
                id="dose_rate_upper_hard_limit"
                effect="solid"
                place="bottom"
                delayHide={300}
                type="error"
              >
                {errors?.dose_rate_upper_hard_limit?.type === 'required' &&
                  t('errors.field__required')}
                {errors?.dose_rate_upper_hard_limit?.type === 'min' &&
                  t('device.errors.not_lower_than', {
                    value: doseRateLimits?.min,
                  })}
                {errors?.dose_rate_upper_hard_limit?.type === 'max' &&
                  t('device.errors.not_greater_than', {
                    value: doseRateLimits?.max,
                  })}
                {errors?.dose_rate_upper_hard_limit?.type ===
                  'increasingOrder' && t('device.errors.order_LHL_LSL_USL_UHL')}
                {errors?.dose_rate_upper_hard_limit?.type === 'precision' &&
                  t('device.errors.precision', {
                    value: precisionDoseRateUHL,
                  })}
              </ReactTooltip>
            )}
            {/* v8 ignore stop -- WAIVER(MICRO) */}
          </div>

          {/* Units */}
          <div className="device-concentration-units">
            {record?.is_complete
              ? record.dose_mode_unit
              : watchDoseModeUnit?.label}{' '}
            {watchWeightBased ? '/ kg' : ''}{' '}
            {record?.is_complete
              ? '/ ' + record.dose_mode_time
              : '/ ' + watchDoseModeTime?.label}
          </div>
        </div>
        {!!wrongLimits(doseRateLimits) && (
          <div className="row pb-3">
            <div className="device-concentration-form__label"></div>
            <div className="device-concentration-form__column">
              <div className="invalid-feedback">
                {t('device.errors.dose_rate_limits__out_of_range')}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DoseRateFields;
